import { api } from './api';
import type { ApiEnvelope, PaginatedEnvelope } from './notification-engine.types';

const BASE = '/api/v1/notification-runs';

/**
 * What one configured recipient resolved to when the notification fired.
 *
 * Stored as `ruleResults` — the fire record keeps the name it was written
 * under, so history stays readable rather than being rewritten to match today.
 */
export interface RuleResult {
  recipientMasterId?: string;
  recipientCode?: string | null;
  outcome?: string | null;
  reasonCode?: string | null;
  resolvedCount?: number | null;
}

/** One person the fire resolved, and where they came from. */
export interface RunRecipient {
  recipientRef?: string;
  partyType?: string | null;
  /** The person's name, resolved on read: the personnel record, else the login id,
   *  else — for an external party, who has neither — the address it was sent to. */
  displayName?: string | null;
  userId?: string | null;
  personnelMasterId?: string | null;
  contact?: Record<string, string> | null;
  state?: string | null;
  recipientMasterId?: string | null;
  selectedActionCode?: string | null;
  actionedAt?: string | null;
}

/** How long a recipient had to act, and whether they did. */
export interface DeliverySla {
  startedAt?: string | null;
  dueAt?: string | null;
  state?: string | null;            // RUNNING | MET | BREACHED
  breachedAt?: string | null;
  metAt?: string | null;
}

/** One recipient × one channel. Each settles independently of the others. */
export interface Delivery {
  /** Who it was for: look them up in `recipients[]`. Not copied here. */
  recipientRef?: string;
  channel?: string | null;
  status?: string | null;
  outcome?: string | null;
  reasonCode?: string | null;
  /** The provider's own words for why this has not gone, or why it failed — set
   *  on an email by the outbox as it retries and settles. */
  lastError?: string | null;
  /** Set by the retry sweep when it stops for good, with why. */
  /**
   * Present only once the retry sweep has stopped trying — its presence IS the
   * fact, which is why there is no boolean inside it.
   */
  retryStopped?: { at?: string | null; reasonCode?: string | null } | null;
  attempts?: Array<Record<string, unknown>>;
  retry?: { count?: number; nextAttemptAt?: string | null } | null;
  sla?: DeliverySla | null;
  renderedContent?: Record<string, unknown> | null;
  /** When the gateway confirmed delivery — set by a receipt or a reply. */
  deliveredAt?: string | null;
  /** WhatsApp only: the gateway's view of this message, kept apart from `status`. */
  whatsapp?: DeliveryWhatsapp | null;
  /** WhatsApp only: messages the recipient sent back, threaded onto this delivery. */
  replies?: DeliveryReply[] | null;
}

/**
 * What the WhatsApp gateway has told us about one sent message.
 *
 * Separate from the delivery's `status`, which only answers "did our send
 * succeed?". Delivered and read are the gateway's word and move only here, so
 * the delivery tag stays SENT while this block records what happened after.
 */
export interface DeliveryWhatsapp {
  to?: string | null;
  toDigits?: string | null;
  /** Our own id, echoed back by the gateway — the key receipts are matched on. */
  referenceId?: string | null;
  /** What the gateway returned on send. For UltraMsg a queue counter, not a message id. */
  providerMessageId?: string | number | null;
  /** The WhatsApp message id, recorded from the first receipt that carries one. */
  ackProviderId?: string | null;
  provider?: string | null;
  /** SENT | QUEUED | DELIVERED | READ | FAILED */
  status?: string | null;
  ackCode?: number | null;
  sentAt?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
  replyWindowMinutes?: number | null;
  lastReplyAt?: string | null;
  replyCount?: number | null;
}

/** One inbound WhatsApp message from the recipient. */
export interface DeliveryReply {
  direction?: string | null;
  /** The sender's chat id as the gateway gave it. */
  from?: string | null;
  fromDigits?: string | null;
  senderName?: string | null;
  body?: string | null;
  messageType?: string | null;
  mediaUrl?: string | null;
  providerMessageId?: string | null;
  replyToMessageId?: string | null;
  /** How the reply was tied to this message — an exact id, or a phone-number guess. */
  matchedBy?: string | null;
  at?: string | null;
  receivedAt?: string | null;
}

/**
 * A new_notifications row — everything one fire did.
 *
 * The configuration is pinned at fire time rather than referenced, so the record
 * keeps saying what was actually sent even after the notification is edited.
 */
export interface NotificationRun {
  _id: string;
  appId?: string | null;
  executionStatus?: string | null;  // CREATED | ACTIVE | COMPLETE | CANCELLED
  outcome?: string | null;
  reasonCode?: string | null;
  configuration?: {
    notificationMasterId?: string;
    notificationTypeId?: string;
    notificationCode?: string | null;
    snapshot?: Record<string, unknown>;
  } | null;
  trigger?: {
    source?: string | null;
    eventType?: string | null;
    correlationId?: string | null;
    payload?: Record<string, unknown>;
  } | null;
  ruleResults?: RuleResult[];
  recipients?: RunRecipient[];
  deliveries?: Delivery[];
  /** Recounted from the deliveries as they settle. `total` is the audience size. */
  counts?: {
    total?: number; delivered?: number; failed?: number; suppressed?: number;
    skipped?: number; read?: number; actioned?: number;
  };
  expiresAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AvailableAction {
  actionCode: string;
  actionType?: string | null;
  isMemoRequired?: boolean;
  style?: Record<string, unknown> | null;
  isDefault?: boolean;
}

export interface NotificationRunListParams {
  notificationMasterId?: string;
  userId?: string;
  correlationId?: string;
  skip?: number;
  limit?: number;
  /** Narrow to one project; omitted keeps every readable project merged. */
  appId?: string;
}

/** What publishing an event matched and dispatched. */
export interface EventPublishResult {
  matched?: number;
  dispatched?: number;
  /** Which schedules matched — named, so a dry run can say who would fire. */
  schedules?: { scheduleId: string; scheduleCode?: string; masterId?: string }[];
  notifications?: string[];
  dryRun?: boolean;
  [key: string]: unknown;
}

export const notificationRunsService = {
  /**
   * Announce that something happened.
   *
   * Matched against EVENT schedules and each match dispatched. An event with no
   * subscriber is a success, not an error — an application should be free to
   * announce things before anything listens.
   *
   * Normally system-to-system: whatever produced the event announces it. The
   * Schedules screen also offers it by hand, to check that an EVENT schedule is
   * wired to the key it claims — which is why `dryRun` exists: it reports who
   * WOULD fire without messaging anybody.
   */
  publishEvent: async (
    eventType: string,
    payload: Record<string, unknown>,
    context?: Record<string, unknown>,
    dryRun = false,
  ): Promise<EventPublishResult> => {
    const response = await api.post<ApiEnvelope<EventPublishResult>>(
      '/api/v1/events', { eventType, payload, context, dryRun },
    );
    return response.data.data;
  },
  list: async (params?: NotificationRunListParams): Promise<PaginatedEnvelope<NotificationRun>> => {
    const response = await api.get<PaginatedEnvelope<NotificationRun>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<NotificationRun> => {
    const response = await api.get<ApiEnvelope<NotificationRun>>(`${BASE}/${id}`);
    return response.data.data;
  },
  /** The actions this viewer may take, re-derived now rather than read off the document. */
  availableActions: async (id: string): Promise<AvailableAction[]> => {
    const response = await api.get<ApiEnvelope<AvailableAction[]>>(`${BASE}/${id}/actions`);
    return response.data.data;
  },
  takeAction: async (id: string, actionCode: string, memo?: string): Promise<Record<string, unknown>> => {
    const response = await api.post<ApiEnvelope<Record<string, unknown>>>(
      `${BASE}/${id}/actions`, { actionCode, memo },
    );
    return response.data.data;
  },
  markRead: async (id: string): Promise<Record<string, unknown>> => {
    const response = await api.post<ApiEnvelope<Record<string, unknown>>>(`${BASE}/${id}/read`);
    return response.data.data;
  },
  cancel: async (id: string): Promise<NotificationRun> => {
    const response = await api.post<ApiEnvelope<NotificationRun>>(`${BASE}/${id}/cancel`);
    return response.data.data;
  },
  /**
   * Re-send the run's FAILED recipients, exactly as they were originally sent, as
   * a new run. Resolves to the ids of the runs produced (empty: nothing failed).
   */
  replay: async (id: string): Promise<{ notifications: string[] }> => {
    const response = await api.post<ApiEnvelope<{ notifications: string[] }>>(
      `${BASE}/${id}/replay`, {},
    );
    return response.data.data;
  },
};
